import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { ImagePlus, X, Camera, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Client-side resize/compress to keep payloads small (matches CheckInDialog pattern)
async function compressImage(file, maxDim = 1280, quality = 0.78) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Failed to read photo"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Failed to decode photo"));
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function CustomerPhotos({ businessId, businessName }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [caption, setCaption] = useState("");
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/businesses/${businessId}/customer-photos`);
      setItems(data.items || []);
    } catch (e) {
      console.warn("Customer photos load failed:", e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [businessId]); // eslint-disable-line

  const pickFile = () => fileRef.current?.click();
  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await compressImage(file);
      setPhoto(dataUrl);
    } catch (err) {
      toast.error(err.message || "Couldn't process photo. Try a different image.");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async () => {
    if (!photo) { toast.error("Pick a photo first."); return; }
    setBusy(true);
    try {
      await api.post(`/businesses/${businessId}/customer-photos`, { photo_data_url: photo, caption });
      toast.success("Photo posted");
      setOpen(false);
      setPhoto(null);
      setCaption("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const removePhoto = async (pid) => {
    setDeletingId(pid);
    try {
      await api.delete(`/businesses/${businessId}/customer-photos/${pid}`);
      setItems((arr) => arr.filter((i) => i.id !== pid));
      toast.success("Photo deleted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const canPost = user?.role === "customer" || (user?.role === "business" && user?.id === businessId) || user?.role === "admin";
  const isBusinessOwner = user?.role === "business" && user?.id === businessId;

  return (
    <div className="mt-10" data-testid="customer-photos-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="overline text-gray-500 inline-flex items-center gap-1.5">
          <ImagePlus size={12} /> Photos
        </h3>
        {canPost && (
          <Button
            onClick={() => setOpen(true)}
            size="sm"
            variant="outline"
            className="rounded-[35px] shadow-sm hover:shadow-md"
            data-testid="customer-photo-add-button"
          >
            <Camera size={13} className="mr-1.5" /> Add a photo
          </Button>
        )}
      </div>

      {loading ? (
        <p className="mt-4 text-sm text-gray-500">Loading…</p>
      ) : items.length === 0 ? (
        <div className="mt-4 border border-dashed border-border rounded-md p-6 text-center" data-testid="customer-photos-empty">
          <ImagePlus size={22} className="mx-auto text-gray-400" />
          <p className="mt-2 text-sm text-gray-600">
            No photos yet.{canPost ? (isBusinessOwner ? " Share photos of your space, work or team." : " Be the first to share your visit.") : ""}
          </p>
        </div>
      ) : (
        <ul className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="customer-photos-grid">
          {items.map((p) => {
            const canDelete = user && (user.id === p.customer_id || isBusinessOwner || user.role === "admin");
            return (
              <li key={p.id} className="group relative rounded-2xl overflow-hidden border border-border bg-white shadow-sm" data-testid={`customer-photo-${p.id}`}>
                <button
                  type="button"
                  onClick={() => setPreview(p)}
                  className="block w-full aspect-square overflow-hidden"
                  aria-label="Open photo"
                >
                  <img
                    src={p.photo_data_url}
                    alt={p.caption || `Photo by ${p.customer_name}`}
                    className="w-full h-full object-cover transition-transform group-hover:scale-105"
                    loading="lazy"
                  />
                </button>
                {p.caption && (
                  <p className="px-3 py-2 text-xs text-gray-700 leading-snug truncate" title={p.caption}>{p.caption}</p>
                )}
                <p className="px-3 pb-2 text-[10px] font-mono uppercase tracking-wider text-gray-400">
                  {p.customer_name}
                </p>
                {canDelete && (
                  <button
                    type="button"
                    onClick={() => removePhoto(p.id)}
                    disabled={deletingId === p.id}
                    className="absolute top-2 right-2 h-7 w-7 rounded-full bg-white/95 border border-border shadow-sm flex items-center justify-center text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Delete photo"
                    data-testid={`customer-photo-delete-${p.id}`}
                  >
                    {deletingId === p.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Upload dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-md max-w-md" data-testid="customer-photo-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Add a photo</DialogTitle>
            <DialogDescription>
              {isBusinessOwner
                ? `Share a photo of your space, team or work to feature on the ${businessName} profile.`
                : `Share what your visit to ${businessName} looked like. Other shoppers will see your photo on this profile.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={onFileChange}
              className="hidden"
              data-testid="customer-photo-file-input"
            />
            {!photo ? (
              <Button type="button" variant="outline" onClick={pickFile} className="rounded-[35px] w-full" data-testid="customer-photo-pick">
                <Camera size={14} className="mr-1.5" /> Choose a photo
              </Button>
            ) : (
              <div className="relative rounded-md overflow-hidden border border-border">
                <img src={photo} alt="Preview" className="w-full aspect-video object-cover" />
                <button
                  type="button"
                  onClick={() => setPhoto(null)}
                  className="absolute top-2 right-2 h-7 w-7 rounded-full bg-white/95 border border-border flex items-center justify-center text-red-600 hover:bg-red-50"
                  title="Clear"
                  data-testid="customer-photo-clear"
                >
                  <X size={13} />
                </button>
              </div>
            )}
            <Input
              type="text"
              placeholder="Optional caption (e.g. 'Best latte in town')"
              maxLength={200}
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              className="rounded-md"
              data-testid="customer-photo-caption"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-[35px]">Cancel</Button>
            <Button
              onClick={submit}
              disabled={!photo || busy}
              className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] shadow-sm hover:shadow-md"
              data-testid="customer-photo-submit"
            >
              {busy ? "Posting…" : "Post photo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lightbox */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="rounded-md max-w-2xl">
          {preview && (
            <>
              <img src={preview.photo_data_url} alt={preview.caption || "Customer photo"} className="w-full rounded-md" />
              {preview.caption && <p className="mt-3 text-sm text-gray-800 leading-relaxed">{preview.caption}</p>}
              <p className="mt-2 text-[11px] font-mono text-gray-500 uppercase tracking-wider">
                {preview.customer_name} · {new Date(preview.created_at).toLocaleDateString()}
              </p>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
